/* eslint-disable react-hooks/exhaustive-deps */
import { Select, Text, TextInput } from "@mantine/core";
import { useStyles } from "./OrgDetails.styles";
import { FC, useState, useEffect, useCallback, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { Button, color } from "@funded-here-interface/common";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Data, Director, UserData } from "../../types/org.type";
import {
  haveDirectors,
  kycStatuses,
  merchants,
} from "@funded-here-interface/common/src/constant/constant";
import Organisation from "../KycTabs/Organization/Organisation";
import AuthRep from "../KycTabs/AuthRep/AuthRep";
import ShareHolder from "../KycTabs/Shareholder/ShareHolder";
import BankDetails from "../KycTabs/BankDetails/BankDetails";
import Documents from "../KycTabs/Documents/Documents";
import {
  EntityTypeEnum,
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import useGetOrgMutation from "../../hooks/useGetOrg";
import useUpdateKyc from "../../hooks/useUpdateKyc";
import useInviteUser from "../../hooks/useInviteUserToOrg";
import useFundingLimit from "../../hooks/useFundingLimit";
import FundLimit from "../KycTabs/FundLimit/FundLimit";
import Agreements from "../KycTabs/Agreements/Agreements";
import { riskValues } from "../../constant/risk";
import { Risk } from "../../constant/enum";
import useOrganizationRisk from "../../hooks/useOrganizationRisk";
import useUpdateSpvKyc from "../../hooks/useUpdateSpvKyc";
import InfoDocWallex from "../KycTabs/InfoDocWallex/InfoDocWallex";
import Regtank from "../KycTabs/Regtank/Regtank";
import Note from "../KycTabs/Note/Note";
import RemarkHistory from "../KycTabs/RemarksHistory/RemarkHistory";
import EmailContent from "../KycTabs/EmailContent/EmailContent";
import EmailContentHistory from "../KycTabs/EmailContent/EmailContentHistory";
import { useRegtankDirectorValidation } from "../../hooks/useRegtankDirectorValidation";

/**
 * Tab configuration interface for dynamic tab rendering
 */
interface TabConfig {
  id: string;
  label: string;
  /** Optional visibility condition function */
  isVisible?: (params: {
    role: Persona;
    signedDocuments: any[];
    selectedValue: string;
    kycStatus?: string;
  }) => boolean;
}

/**
 * Tab configuration array
 * Centralizes tab definitions to eliminate duplicate JSX
 */
const TAB_CONFIGS: TabConfig[] = [
  { id: "organisation", label: "Organisation" },
  { id: "auth-rep", label: "Auth Rep/Admin" },
  {
    id: "directors",
    label: "Directors",
    isVisible: ({ role }) => haveDirectors.includes(role),
  },
  { id: "bank-details", label: "Bank Details" },
  { id: "documents", label: "Documents" },
  {
    id: "agreements",
    label: "Agreements",
    isVisible: ({ signedDocuments }) => signedDocuments.length > 0,
  },
  { id: "regtank", label: "Regtank Verification" },
  { id: "remarks", label: "Remarks" },
  { id: "remarks-history", label: "Remarks History" },
  {
    id: "wallex",
    label: "Info & Doc to Wallex",
    isVisible: ({ role, selectedValue, kycStatus }) =>
      [Persona.INVESTOR, Persona.MERCHANT, Persona.MERCHANT_SPV].includes(
        role
      ) &&
      selectedValue === KYCStatus.APPROVED &&
      kycStatus === "approved",
  },
];

interface IProps {
  close: () => void;
  id: number;
  role: Persona;
  companyName?: string;
  refetchList: () => void;
}

const OrgDetails: FC<IProps> = ({
  close,
  id,
  role,
  refetchList,
  companyName,
}) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [tab, setTab] = useState("organisation");
  const [data, setdata] = useState<Data>();
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<UserData>();
  const [agreedToMembership, setAgreedToMembership] = useState<boolean>(false);
  const [agreedToSpvMembership, setAgreedToSpvMembership] =
    useState<boolean>(false);
  const [allUsersData, setAllUsersData] = useState<UserData[]>();
  const { token, regtankOnboardingFeatureFlag } = useSelector(
    (state: RootState) => state.auth
  );
  const getOrgMutation = useGetOrgMutation();
  const updateKycMutation = useUpdateKyc();
  const useUpdateSpvKycMutation = useUpdateSpvKyc();
  const useInviteUserMutation = useInviteUser();
  const useFundingLimitMutaiton = useFundingLimit();
  const useOrganizationRiskMutation = useOrganizationRisk();
  const { classes } = useStyles();
  const isSpvOrg = role === Persona.MERCHANT_SPV;
  const [bmcaSigning, setBmcaSigning] = useState(0);
  const [selectedValue, setSelectedValue] = useState<string>(
    data?.kycStatus || ""
  );
  const [selectedSpvKyc, setSelectedSpvKyc] = useState<string>(
    data?.spv?.kycStatus || ""
  );
  const [selectedRisk, setSelectedRisk] = useState<Risk | undefined>();
  const [disableSaveBtn, setDisableSaveBtn] = useState(false);

  const [orgNote, setOrgNote] = useState<string>("");
  const [spvNote, setSpvNote] = useState<string>("");
  const [directorEmails, setDirectorEmails] = useState<string[]>([]);
  const headingRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);

  /**
   * Shows a toast error and scrolls to the heading
   */
  const showErrorAndScrollToTop = useCallback((message: string) => {
    toast.error(message);
    headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (data?.kycStatus) {
      setSelectedValue(data.kycStatus);
    }

    if (data?.spv?.kycStatus) {
      setSelectedSpvKyc(data?.spv?.kycStatus);
    }
  }, [data?.kycStatus, data?.spv?.kycStatus]);

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
  };

  const handleSpvKycSelectChange = (value: string) => {
    setSelectedSpvKyc(value);
  };

  const handleRiskSelect = (risk: Risk) => {
    setSelectedRisk(risk);

    useOrganizationRiskMutation.mutate(
      { token, risk, orgId: data?.id! },
      {
        onError: (e) => {
          showErrorAndScrollToTop((e as Error).message);
        },
      }
    );
  };

  useEffect(() => {
    getOrgMutation.mutate(
      {
        token,
        id: `${id}`,
      },
      {
        onSuccess: (data) => {
          setDisableSaveBtn(false);

          // Check for pending Master SKU Agreement signatures
          if (data.signDocument) {
            const masterSkuAgreement = data.signDocument.find(
              (doc: any) => doc.documentName === "Master SKU Agreement"
            );

            const hasPendingSigner = masterSkuAgreement?.personSigning.some(
              (signer: any) => signer.status === "pending"
            );

            if (hasPendingSigner) {
              data.documentsUpload?.signedDocuments.push({
                docName: "Master SKU Agreement",
                path: "",
              });
              setBmcaSigning(id);
              setDisableSaveBtn(true);
            }
          }

          setdata(data);
          setSelectedRisk(data?.risk);
          setAllUsersData(data.user);

          // Set userData to first user by default
          setUserData(data.user[0]);
          setUserId(data.user[0]?.id);

          // Set director emails from board of directors
          const directorEmails = (data?.boardOfDirectors ?? []).map(
            (director: Director) => director.email
          );
          setDirectorEmails(directorEmails);

          // Check membership agreement status across all users
          const hasAgreedToMembership = data.user.some(
            (user: UserData) => user?.agreedToMembershipAgreement === true
          );
          const hasAgreedToSpvMembership = data.user.some(
            (user: UserData) => user?.agreedToSpvMembershipAgreement === true
          );

          setAgreedToMembership(hasAgreedToMembership);
          setAgreedToSpvMembership(hasAgreedToSpvMembership);
        },
        onError: (e) => {
          showErrorAndScrollToTop((e as Error).message);
        },
      }
    );
  }, [tab]);
  const [fundingLimit, setFundingLimit] = useState<string>("");

  const inviteShareholder = async (onBoardingUserType: Persona) => {
    executeMutation(useInviteUserMutation, {
      token,
      orgId: id,
      onBoardingUserType,
      isSpvOrg,
    });
  };

  const handleMerchant = async (orgRoleId: number) => {
    if (fundingLimit || selectedValue === KYCStatus.APPROVED) {
      if (selectedValue !== KYCStatus.APPROVED) {
        showErrorAndScrollToTop("Must approve kyc to set funding limit");
        setIsLoading(false);
        return;
      }

      if (!fundingLimit || fundingLimit === "0") {
        showErrorAndScrollToTop("Must set funding limit when approving KYC");
        setIsLoading(false);
        return;
      }

      // Set funding limit, then update KYC status on success
      executeMutation(
        useFundingLimitMutaiton,
        {
          token,
          orgRoleId: orgRoleId as number,
          creditLimit: fundingLimit,
        },
        () => {
          // Chain updateKyc mutation after funding limit is set
          executeMutation(updateKycMutation, {
            orgRoleId,
            token,
            kycStatus: selectedValue,
          });
        }
      );
    } else {
      updateKyc(orgRoleId);
    }
  };

  const updateKyc = async (orgRoleId: number) => {
    executeMutation(updateKycMutation, {
      orgRoleId: orgRoleId as number,
      token,
      kycStatus: selectedValue as KYCStatus,
    });
  };

  const updateSpvKyc = async (orgRoleId: number) => {
    executeMutation(useUpdateSpvKycMutation, {
      orgRoleId,
      token,
      kycStatus: selectedSpvKyc as KYCStatus,
    });
  };

  // Use custom hook to validate director Regtank status
  const regtankValidation = useRegtankDirectorValidation(
    directorEmails,
    allUsersData,
    role
  );

  /**
   * Validates Regtank requirements for approval
   * @returns true if validation passes, false if validation fails (also shows toast error)
   */
  const validateRegtankForApproval = useCallback((): boolean => {
    if (!regtankOnboardingFeatureFlag) {
      return true; // Skip validation if feature flag is off
    }

    if (!regtankValidation.allDirectorsApproved) {
      showErrorAndScrollToTop(
        `Directors ${regtankValidation.pendingDirectors.join(
          ", "
        )} have not completed Regtank onboarding identity verification.`
      );
      return false;
    }

    return true;
  }, [
    regtankOnboardingFeatureFlag,
    regtankValidation.allDirectorsApproved,
    regtankValidation.pendingDirectors,
    showErrorAndScrollToTop,
  ]);

  /**
   * Generic mutation executor with standardized loading and error handling
   * Eliminates duplicate onSuccess/onError callbacks across all mutations
   * @param mutation - The React Query mutation object to execute
   * @param params - Parameters to pass to the mutation
   * @param onSuccessCallback - Optional additional logic to run on success
   */
  const executeMutation = useCallback(
    <TParams, TData>(
      mutation: {
        mutate: (
          params: TParams,
          callbacks: {
            onSuccess?: (data: TData) => void;
            onError?: (error: unknown) => void;
          }
        ) => void;
      },
      params: TParams,
      onSuccessCallback?: () => void
    ) => {
      setIsLoading(true);

      mutation.mutate(params, {
        onSuccess: (data) => {
          isSavingRef.current = false;
          if (onSuccessCallback) {
            onSuccessCallback();
          } else {
            setIsLoading(false);
            refetchList();
            close();
          }
        },
        onError: (e) => {
          isSavingRef.current = false;
          setIsLoading(false);
          showErrorAndScrollToTop((e as Error).message);
        },
      });
    },
    [close, refetchList, showErrorAndScrollToTop]
  );

  const handleSave = async () => {
    // Synchronous ref guard prevents double-click (useState is vulnerable to React 18 batching)
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    // Prevent multiple clicks by setting loading immediately
    if (isLoading) {
      isSavingRef.current = false;
      return;
    }
    setIsLoading(true);

    const orgRoleId = userData?.orgRole?.[0]?.id;

    // Prevent manual status update when in PENDING_RESUBMISSION
    if (data?.kycStatus === KYCStatus.PENDING_RESUBMISSION) {
      showErrorAndScrollToTop(
        "Manual status update not allowed. Awaiting resubmission of the onboarding form by the onboarding user."
      );
      setIsLoading(false);
      isSavingRef.current = false;
      return;
    }

    if (
      data?.spv &&
      role === Persona.MERCHANT_SPV &&
      selectedSpvKyc !== KYCStatus.PENDING_APPROVAL &&
      selectedSpvKyc !== data?.spv?.kycStatus
    ) {
      updateSpvKyc(orgRoleId as number);

      return;
    }

    if (
      selectedValue === KYCStatus.PENDING_APPROVAL ||
      selectedValue === data?.kycStatus
    ) {
      setIsLoading(false);
      isSavingRef.current = false;
      close();

      return;
    }

    // Validate Regtank requirements before approval
    if (merchants.includes(role)) {
      if (selectedValue === KYCStatus.APPROVED) {
        if (!validateRegtankForApproval()) {
          setIsLoading(false);
          isSavingRef.current = false;
          return;
        }
      }
      handleMerchant(orgRoleId as number);
      return;
    }

    if (
      (userData?.entityType === EntityTypeEnum.INSTITUTION ||
        role === Persona.ISPV) &&
      selectedValue === KYCStatus.APPROVED
    ) {
      if (!validateRegtankForApproval()) {
        setIsLoading(false);
        isSavingRef.current = false;
        return;
      }
    }

    updateKyc(orgRoleId as number);
  };

  const signedDocuments = data?.documentsUpload?.signedDocuments
    ? data.documentsUpload.signedDocuments.filter((doc) => {
        if (doc.docName === "membership_agreement" && !agreedToMembership) {
          return false;
        }

        if (
          doc.docName === "spv_membership_agreement" &&
          !agreedToSpvMembership
        ) {
          return false;
        }
        return true;
      })
    : [];

  /**
   * Renders content for the active tab
   * Centralizes all tab content rendering logic
   */
  const renderTabContent = useCallback(() => {
    switch (tab) {
      case "organisation":
        return (
          <Organisation
            data={data}
            userData={userData}
            role={role}
            spvData={data?.spv}
          />
        );
      case "auth-rep":
        return (
          <AuthRep
            data={data}
            userData={userData}
            role={role}
            spvData={data?.spv}
          />
        );
      case "directors":
        return (
          <div className={classes.shareHolderWrapper}>
            <ShareHolder data={data} spvData={data?.spv} />
          </div>
        );
      case "bank-details":
        return <BankDetails userData={userData} />;
      case "documents":
        return <Documents data={data} userData={userData} role={role} />;
      case "agreements":
        return signedDocuments.length > 0 ? (
          <Agreements
            bmcaSigning={bmcaSigning}
            signedDocuments={signedDocuments}
          />
        ) : null;
      case "regtank":
        return <Regtank orgData={data} userData={allUsersData} />;
      case "remarks":
        return (
          <div>
            <Text className={classes.subHeadingText}>Internal Remarks</Text>
            <Note userId={userId} initialNote={orgNote} type="regular" />
            {data?.spv && (
              <div style={{ marginTop: "20px" }}>
                <Text className={classes.subHeadingText}>
                  Internal Remarks (SPV)
                </Text>
                <Note userId={userId} initialNote={spvNote} type="spv" />
              </div>
            )}
            <div style={{ marginTop: "20px" }}>
              <Text className={classes.subHeadingText}>
                Remarks for Onboarding Details Resubmission
              </Text>
              <EmailContent
                data={data}
                userData={userData}
                companyName={companyName || "Default Company Name"}
              />
            </div>
          </div>
        );
      case "remarks-history":
        return (
          <div>
            <Text className={classes.subHeadingText}>Internal Remarks</Text>
            <RemarkHistory userId={userId} type="regular" />
            {data?.spv && (
              <div style={{ marginTop: "20px" }}>
                <Text className={classes.subHeadingText}>
                  Internal Remarks (SPV)
                </Text>
                <RemarkHistory userId={userId} type="spv" />
              </div>
            )}
            <div style={{ marginTop: "20px" }}>
              <Text className={classes.subHeadingText}>
                Remarks for Onboarding Details Resubmission
              </Text>
              <EmailContentHistory userId={userId} />
            </div>
          </div>
        );
      case "wallex":
        return (
          <InfoDocWallex data={data} userData={userData} user={allUsersData} />
        );
      default:
        return null;
    }
  }, [
    tab,
    data,
    userData,
    role,
    signedDocuments,
    bmcaSigning,
    userId,
    orgNote,
    spvNote,
    companyName,
    allUsersData,
    classes.shareHolderWrapper,
    classes.subHeadingText,
  ]);

  return (
    <div className={classes.OuterContainer} ref={headingRef}>
      <div className={classes.innerContainer}>
        <ToastContainer />

        <AiOutlineClose
          onClick={() => {
            close();
            setSelectedValue("");
          }}
          className={classes.closeIcon}
        />

        <Text className={classes.heading}>Organisation KYC Details</Text>
        <div className={classes.SubHeadingOutterContainer}>
          {merchants.includes(role) && (
            <div className={classes.subHeadingContainer}>
              <Text className={classes.subHeadingText}>Funding Limit</Text>
              <TextInput
                size="md"
                className={classes.commentInputForFund}
                value={fundingLimit}
                placeholder={
                  userData?.orgRole?.[0]?.creditLimit
                    ? `${userData.orgRole[0].creditLimit}`
                    : "0"
                }
                onChange={(e) => setFundingLimit(e.target.value)}
                variant="filled" // Softer input style
              />
            </div>
          )}
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>KYC Status</Text>
            <Select
              className={classes.KycSelect}
              data={kycStatuses.map((status) => ({
                value: status,
                label: status,
                disabled: status === KYCStatus.PENDING_RESUBMISSION,
              }))}
              value={selectedValue}
              onChange={handleSelectChange}
              size="md"
              variant="filled"
              radius="md"
              disabled={data?.kycStatus === KYCStatus.PENDING_RESUBMISSION}
              styles={{
                dropdown: {
                  minWidth: "200px", // Ensure dropdown is wide enough for longer text
                },
                item: {
                  whiteSpace: "nowrap", // Prevent text wrapping in dropdown items
                },
              }}
            />
            {data?.kycStatus === KYCStatus.PENDING_RESUBMISSION && (
              <Text size="sm" color="dimmed" mt={4}>
                Status locked. Awaiting user resubmission.
              </Text>
            )}
          </div>
          {data?.spv && (
            <div className={classes.subHeadingContainer}>
              <Text className={classes.subHeadingText}>SPV KYC</Text>
              <Select
                className={classes.KycSelect}
                data={kycStatuses}
                value={selectedSpvKyc}
                onChange={handleSpvKycSelectChange}
                size="md"
                variant="filled"
                radius="md"
              />
            </div>
          )}
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>Customer Risk</Text>
            <Select
              className={classes.KycSelect}
              data={riskValues}
              value={selectedRisk}
              placeholder="Select Risk"
              onChange={handleRiskSelect}
              size="md"
              variant="filled"
              radius="md"
            />
          </div>
        </div>
        <div>
          <ul className={classes.listContainer}>
            {TAB_CONFIGS.filter((tabConfig) => {
              // Show tab if no visibility condition exists, or if condition is met
              if (!tabConfig.isVisible) return true;
              return tabConfig.isVisible({
                role,
                signedDocuments,
                selectedValue,
                kycStatus: data?.kycStatus,
              });
            }).map((tabConfig) => (
              <li
                key={tabConfig.id}
                onClick={() => setTab(tabConfig.id)}
                className={`${classes.listText} ${
                  tab === tabConfig.id && classes.stepActive
                }`}
              >
                {tabConfig.label}
              </li>
            ))}
          </ul>
          <div className={classes.contentContainer} data-tab={tab}>
            {renderTabContent()}
          </div>
          {tab === "organisation" && (
            <div className={classes.buttonWrapper}>
              <Button
                disabled={isLoading || disableSaveBtn}
                onClick={handleSave}
                backgroundColor={color.FHADMIN}
                textColor={color.WHITE}
                width="80%"
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgDetails;
